/**
 * Wrapper que detecta el bookingMode del negocio y renderiza el widget
 * correcto: el flujo clásico de mesas o el de citas.
 *
 * Solo hace una llamada ligera a /api/public/organizations/:slug y deja
 * que cada subflujo recargue sus propios datos.
 */

import { useEffect, useState } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";
import App from "./App";
import AppointmentsApp from "./pages/AppointmentsApp";
import ClassesApp from "./pages/ClassesApp";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4000/api";

export default function WidgetEntry() {
  const { slug: rawSlug } = useParams<{ slug: string }>();
  const location = useLocation();
  // Business websites sometimes paste the link with a trailing space
  // (`/los-naranjos-lanzarote%20`) → "Restaurante no encontrado". Trim it
  // and redirect so every subflow reads the clean slug from the URL.
  const slug = rawSlug?.trim();
  const needsCleanUrl = !!slug && slug !== rawSlug;
  const [mode, setMode] = useState<
    "tables" | "appointments" | "classes" | null
  >(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug || needsCleanUrl) return;
    fetch(`${API_URL}/public/organizations/${slug}`)
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data?.message || "Negocio no encontrado");
        }
        // Sede con el módulo de clases: flujo propio (manda sobre el modo).
        if (data.organization?.classesEnabled === true) {
          setMode("classes");
          return;
        }
        const m = data.organization?.bookingMode;
        setMode(m === "appointments" ? "appointments" : "tables");
      })
      .catch((err) => setError(err.message ?? "Error al cargar el negocio"));
  }, [slug, needsCleanUrl]);

  if (needsCleanUrl) {
    return (
      <Navigate
        to={{
          pathname: `/${slug}`,
          search: location.search,
          hash: location.hash,
        }}
        replace
      />
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white border border-red-200 rounded-2xl p-8 max-w-md text-center shadow">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-gray-900">{error}</h1>
        </div>
      </div>
    );
  }

  if (!mode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (mode === "classes") return <ClassesApp />;
  return mode === "appointments" ? <AppointmentsApp /> : <App />;
}
